// ---------------------------------------------------------------------------
// industry.zh.ts — Chinese translations for all 5 industry landing pages
// ---------------------------------------------------------------------------
import type { IndustryText } from './industry.content';

export const INDUSTRY_ZH: Record<string, IndustryText> = {
  'hotel-software-for-small-hotels': {
    propertyType: '小型酒店业主',
    heroTitle: '专为有远大抱负的小型酒店设计的酒店软件',
    heroSubtitle: '为10-50间客房物业设计的AI驱动云端PMS。AI动态定价、智能客房服务和AI礼宾为小型酒店提供与大型连锁同等的智能——无需IT团队。',
    painPoints: [
      { title: '到处都是手动操作', description: '笔记本、电子表格和手写记录导致错误、遗漏预订和浪费时间。' },
      { title: '昂贵的遗留系统', description: '企业级PMS解决方案每年花费数千美元，需要小型酒店负担不起的专职IT团队。' },
      { title: '没有内部技术团队', description: '没有技术团队，解决软件问题意味着昂贵的顾问和漫长的停机时间。' },
      { title: '过度依赖OTA', description: '每笔OTA预订支付15-25%的佣金，侵蚀本已微薄的利润率。' },
    ],
    solutionFeatures: [
      { title: '简单而强大的PMS', description: '拖放式入住、直接预订和实时可用性的房间看板。无需培训。', highlight: '无需培训' },
      { title: '自动客房清洁', description: '退房时自动分配任务、实时房间状态跟踪、取消纸质清洁单。' },
      { title: '直接预订引擎', description: '网站上的零佣金预订小部件。直接接受付款并建立客人数据库。' },
      { title: '基础收入工具', description: '简单的入住率和RevPAR仪表板。设置季节性定价和最低住宿规则。' },
      { title: '移动访问', description: '从任何地方管理您的物业。通过手机查看房间状态、审批预订和查看报告。' },
      { title: '云端且始终在线', description: '无需服务器机房、无需安装、无需维护。自动更新和99.9% SLA。' },
      { title: 'AI驱动智能', description: 'AI动态定价根据需求调整房价，AI礼宾全天候处理客人咨询，智能客房服务自动分配房间——小型酒店价格的企业级AI。' },
    ],
    caseStudy: { challenge: '一家30间客房的精品酒店用笔记本和Excel管理一切。频繁双重预订、无法了解入住趋势、90%的预订通过20%佣金的OTA。', solution: '部署HotelStack.io入门计划，包含PMS、直接预订引擎和OTA渠道同步。36小时内完成全面迁移并培训员工。', results: ['通过实时OTA同步完全消除双重预订', '6个月内直接预订从10%增长到40%', '入住时间从8分钟缩短到2分钟', '手动记录时间节省超过70%'] },
    statsLabels: ['节省时间', '更多直接预订', '设置时间', '起步价格'],
    testimonial: { quote: '我们在2天内从笔记本转变为完全数字化的酒店。仅预订引擎就在去年为我们节省了数千美元的OTA佣金。这是我们做过的最好的投资。', role: '业主', hotel: 'The Harbour Inn，里斯本' },
    faqItems: [
      { question: 'HotelStack.io对小型酒店来说经济实惠吗？', answer: '是的。我们的计划从每月49美元起，最多50间客房。无设置费、无隐藏费用、14天免费试用。' },
      { question: '使用HotelStack.io需要IT团队吗？', answer: '完全不需要。HotelStack.io是为非技术酒店人设计的云平台。我们的入职团队负责一切。' },
      { question: '可以用手机管理酒店吗？', answer: '可以。平台完全响应式，适用于任何设备。' },
      { question: '多快能开始收到直接预订？', answer: 'BookMyRoom.io上的直接预订引擎在注册后48小时内上线。' },
    ],
    relatedIndustries: [
      { label: '精品酒店', description: '独特物业的个性化客人体验' },
      { label: '青年旅舍', description: '经济住宿的床位级库存管理' },
      { label: '连锁酒店', description: '大规模多物业管理' },
      { label: '度假村', description: '餐饮、水疗和活动管理' },
    ],
  },
  'hotel-software-for-resorts': {
    propertyType: '度假村和水疗运营商',
    heroTitle: '打造世界级体验的度假村管理软件',
    heroSubtitle: '客房、餐厅、水疗和活动的AI驱动度假村管理。AI需求预测优化季节性定价，智能客房服务智能分配区域，AI礼宾跨出口处理客人请求。',
    painPoints: [
      { title: '复杂的餐饮运营', description: '多个餐厅、客房服务、泳池吧和宴会需要互不相通的独立系统。' },
      { title: '水疗和活动排程', description: '手动管理水疗预约、活动时间表和设备租赁导致双重预订和收入损失。' },
      { title: '活动协调', description: '在大型物业中协调旅游、健康项目和娱乐活动，没有合适的工具就是后勤噩梦。' },
      { title: '季节性需求波动', description: '度假村面临极端的季节性。没有动态定价，旺季损失收入或淡季空置。' },
    ],
    solutionFeatures: [
      { title: 'POS和餐饮系统', description: '餐厅、酒吧和客房服务的完整POS。菜单管理、KOT跟踪和桌位布局。', highlight: '多网点' },
      { title: '餐厅管理', description: '管理多个餐厅的预订。餐食计划、饮食要求和宴会从一个面板管理。' },
      { title: '活动排程', description: '安排和管理度假村活动、水疗理疗和旅游。自动与客人账户同步。' },
      { title: '动态季节定价', description: '按季节的自动定价规则。收入管理工具全年最大化RevPAR。' },
      { title: '团体预订引擎', description: '管理婚礼、会议和旅游团，包含房间列表、取消跟踪和统一账单。' },
      { title: '大规模客房清洁', description: '大型物业的分区清洁分配。布草跟踪、检查清单和任务状态。' },
      { title: 'AI需求与定价', description: 'ML驱动的需求预测预测90天内入住率。AI动态定价根据季节、活动和竞争对手变化自动调整房价以最大化RevPAR。' },
    ],
    caseStudy: { challenge: '120间客房的豪华度假村，有3个餐厅、水疗中心和泳池吧。独立的POS系统导致未计费消费，手动水疗排程导致双重预订。', solution: '部署HotelStack.io企业版，统一所有网点POS、集成水疗排程和动态季节定价规则。', results: ['消除未计费消费，餐饮收入增长25%', '客房清洁周期改善30%', '自动季节定价使RevPAR提升15%', '统一账单使客人满意度达98%'] },
    statsLabels: ['餐饮收入增长', '清洁改善', 'RevPAR提升', '客人满意度'],
    testimonial: { quote: '管理三个餐厅和一个水疗中心曾需要四个不同的系统。现在一切自动流入单一客人账户。未计费消费降为零。', role: '度假村总监', hotel: 'Azure Coast Resort，普吉岛' },
    faqItems: [
      { question: 'HotelStack.io能管理多个餐厅吗？', answer: '可以。POS系统支持无限网点。每个网点有自己的菜单和桌位布局，但所有消费同步到统一客人账户。' },
      { question: '支持水疗和活动预约吗？', answer: '支持。活动排程管理水疗理疗、旅游、健身课程和设备租赁，自动与客人账户同步。' },
      { question: '季节定价如何工作？', answer: '定义带开始和结束日期的价格季节。设置旺季附加费、淡季折扣和最低住宿要求的自动规则。' },
      { question: '能处理团体预订和活动吗？', answer: '可以。团体预订模块支持房间块、房间列表、取消跟踪、团体账单和活动协调。' },
    ],
    relatedIndustries: [
      { label: '精品酒店', description: '历史物业的个性化体验' },
      { label: '连锁酒店', description: '度假村集团的多物业管理' },
      { label: '小型酒店', description: '小型物业的经济实惠PMS' },
      { label: '青年旅舍', description: '经济住宿管理工具' },
    ],
  },
  'hotel-software-for-boutique-hotels': {
    propertyType: '精品和历史酒店业主',
    heroTitle: '与您精品酒店身份相匹配的酒店软件',
    heroSubtitle: '精品酒店规模的AI个性化。AI客人画像自动学习偏好，AI情感分析实时监控评论，AI礼宾全天候提供定制服务。',
    painPoints: [
      { title: '每位客人都期望个性化', description: '精品酒店客人为个人化服务支付溢价。没有CRM跟踪偏好，无法提供预期的亲密感。' },
      { title: '历史建筑限制', description: '独特的房间配置和文物建筑限制使标准PMS模板无法使用。' },
      { title: '团队小期望大', description: '员工有限，每分钟都很重要。手动沟通、清洁和计费浪费宝贵时间。' },
      { title: '极高的客人期望', description: '精品酒店客人留下详细评论。一个被遗忘的偏好可能损害精心建立的声誉。' },
    ],
    solutionFeatures: [
      { title: '带偏好的客人CRM', description: '跟踪房间偏好、饮食需求、纪念日和过往住宿。入住时自动显示备注。', highlight: '个性化' },
      { title: '忠诚度计划', description: '创建自己的积分、奖励和独家优惠的等级系统。将一次性访客转化为终身大使。' },
      { title: '自动通讯', description: '到达前消息、住宿期间跟进和退房后评论请求。匹配品牌声音的模板。' },
      { title: '品牌化预订', description: '与物业美学相匹配的白标预订引擎。自定义颜色、字体和图片。' },
      { title: '房间独立配置', description: '为每间客房定义独特的设施、特征和定价。适合每间客房都不同的历史物业。' },
      { title: '声誉分析', description: '跟踪所有平台的评论分数。识别趋势、回应反馈、衡量改进效果。' },
      { title: 'AI礼宾与客人画像', description: 'AI聊天机器人以25种以上语言处理客人请求。AI客人画像自动从过往入住、评论和消息中捕获偏好，提供超个性化服务。' },
    ],
    caseStudy: { challenge: '25间主题客房的改建别墅。业主无法跟踪客人偏好，没有忠诚度计划，65%的预订通过OTA。', solution: '部署HotelStack.io，包含客人CRM、定制忠诚度计划、自动通讯和品牌预订引擎。', results: ['一年内回头客从20%增长到50%', '所有平台平均评分4.9', '直接预订从35%增长到65%', '所有直接预订零OTA佣金'] },
    statsLabels: ['回头客', '平均评分', '直接预订', '直接预订佣金'],
    testimonial: { quote: '我们的客人感到特别。当回头客到达，我们已经知道他们喜欢的房间、枕头类型和最爱的咖啡时，他们的脸上会绽放光彩。这就是精品酒店待客之道的精髓。', role: '业主兼总经理', hotel: 'Villa Toscana，佛罗伦萨' },
    faqItems: [
      { question: '可以自定义预订引擎以匹配我的品牌吗？', answer: '可以。白标引擎支持自定义颜色、字体、标志和图片，并与您的网站集成。' },
      { question: 'CRM如何帮助个性化？', answer: 'CRM存储客人偏好、饮食要求、房间选择和特殊场合。员工在入住时自动查看这些信息。' },
      { question: '可以为精品酒店设置忠诚度计划吗？', answer: '可以。创建品牌忠诚度等级、定义积分规则、设置独家奖励。' },
      { question: '能处理不同配置的房间吗？', answer: '当然。为每间客房定义独特的设施、床型配置、景观和定价。' },
    ],
    relatedIndustries: [
      { label: '小型酒店', description: '经济实惠的云端PMS' },
      { label: '度假村', description: '复杂的餐饮和活动管理' },
      { label: '连锁酒店', description: '多物业品牌管理' },
      { label: '青年旅舍', description: '经济型管理工具' },
    ],
  },
  'hotel-software-for-hostels': {
    propertyType: '青年旅舍和经济住宿管理者',
    heroTitle: '具备床位级控制的青年旅舍管理软件',
    heroSubtitle: 'AI驱动的青年旅舍PMS，具备床位级控制。AI动态定价最大化每床收入，AI需求预测预测高峰期，智能自动分配高效填充宿舍。',
    painPoints: [
      { title: '床位级库存缺口', description: '大多数PMS只支持房间级管理。青年旅舍需要床位级跟踪。' },
      { title: '高周转率混乱', description: '客人每天进出，手动流程在前台造成瓶颈。' },
      { title: '价格敏感的旅行者', description: '经济旅行者在数十个OTA上比较价格。没有即时价格一致性就会失去预订。' },
      { title: '漫长的入住队列', description: '成群结队的旅行者造成瓶颈。手动收集证件、收款和分配床位耗时太长。' },
    ],
    solutionFeatures: [
      { title: '床位级房间看板', description: '显示所有宿舍每张床的可视化网格。拖放分配、交换和锁定单个床位。', highlight: '床位级' },
      { title: '快速入住', description: '证件扫描、数据自动填充、收款和床位分配在3分钟内完成。' },
      { title: '宿舍团体预订', description: '为团体预订整个宿舍或多个床位。管理房间列表和团体账单。' },
      { title: 'OTA同步和价格一致性', description: '与Hostelworld、Booking.com和100多个渠道双向同步。即时价格和可用性更新。' },
      { title: '基础咖啡厅POS', description: '公共区域咖啡厅或酒吧的简单POS。消费自动添加到客人账户。' },
      { title: '快速报告', description: '每日入住率、收入和退房报告。高效管理旅舍所需的数据。' },
      { title: 'AI定价与预测', description: 'AI根据需求、季节性和竞争对手定价设置每床价格。30/60/90天需求预测帮助规划人员配置和促销活动。' },
    ],
    caseStudy: { challenge: '20个宿舍80张床位的青年旅舍，用Excel跟踪床位，60%预订通过高佣金OTA，高峰期入住排队10分钟。', solution: '部署HotelStack.io，包含床位级看板、快速入住、OTA渠道同步和直接预订页面。', results: ['平均入住时间从10分钟缩短到不到3分钟', '6个月内直接预订从40%增长到60%', '从单一仪表板管理所有20个宿舍80张床位', '动态价格管理实现90%平均入住率'] },
    statsLabels: ['入住时间', '直接预订', '管理床位', '平均入住率'],
    testimonial: { quote: '终于有软件理解青年旅舍不只是小型酒店。床位级管理改变了一切。前台团队可以在几分钟内处理20人的团体。', role: '创始人', hotel: 'Nomad Social Hostel，巴塞罗那' },
    faqItems: [
      { question: 'HotelStack.io支持床位级库存吗？', answer: '支持。房间看板支持宿舍的床位管理和私人房间的房间管理。' },
      { question: '与Hostelworld和旅舍OTA兼容吗？', answer: '是的。我们集成了Hostelworld、Booking.com、HostelBookers和100多个OTA渠道。' },
      { question: '快速入住有多快？', answer: '每位客人不到3分钟。证件扫描、自动填充、支付处理和床位分配。' },
      { question: '有适合旅舍的经济计划吗？', answer: '有。我们的计划从每月49美元起，支持最多50间房或床位单元。提供区域定价。' },
    ],
    relatedIndustries: [
      { label: '小型酒店', description: '经济住宿的云端PMS' },
      { label: '精品酒店', description: '个性化客人体验' },
      { label: '度假村', description: '餐饮和多样活动管理' },
      { label: '连锁酒店', description: '集中式多物业管理' },
    ],
  },
  'hotel-software-for-hotel-chains': {
    propertyType: '连锁酒店运营商',
    heroTitle: '为成长中的连锁打造的多物业酒店软件',
    heroSubtitle: 'AI驱动的多物业管理。跨物业AI收入智能、集中AI动态定价、投资组合级需求预测以及连锁每个位置的AI异常检测。',
    painPoints: [
      { title: '每个物业系统不同', description: '每个物业使用不同软件意味着没有统一视图、数据格式不一致和昂贵的许可证。' },
      { title: '没有集中数据', description: '没有汇总报告，连锁管理者只能盲目操作。比较物业间的表现需要手动汇总数据。' },
      { title: '定价不一致', description: '没有集中定价管理，各物业在渠道间发布矛盾的价格，损害品牌诚信。' },
      { title: '到处重复工作', description: '价格变更、政策更新和品牌指南必须在每个物业手动复制。' },
    ],
    solutionFeatures: [
      { title: '连锁管理仪表板', description: '所有物业的全局视图。每个地点的入住率、收入和运营指标在一个屏幕上。', highlight: '集中管理' },
      { title: '跨物业分析', description: '跨物业比较RevPAR、ADR、入住率和客人满意度。识别表现最佳和需要关注的物业。' },
      { title: '集中定价管理', description: '从单一界面向所有物业和渠道设置和推送价格。保证品牌级价格一致性。' },
      { title: '基于角色的员工访问', description: '连锁、物业和部门级角色。对整个组合中谁能看到什么的精细控制。' },
      { title: '汇总合规', description: '区域税务报告、客人登记和汇总审计追踪。一键生成整个连锁的合规报告。' },
      { title: '品牌配置', description: '在所有物业定义和执行品牌标准。一致的预订页面、邮件模板和客人沟通。' },
      { title: '跨物业AI智能', description: 'AI收入智能比较所有物业的表现。投资组合级需求预测、集中AI定价和异常检测在影响客人之前发出警报。' },
    ],
    caseStudy: { challenge: '5个国家12个物业的成长中连锁，每个使用不同的PMS。没有汇总报告、OTA定价不一致、企业费率管理需要手动更新。', solution: '将所有12个物业迁移到HotelStack.io企业连锁计划。集中定价管理、汇总仪表板、统一客人CRM和基于角色的访问。', results: ['集中运营减少40%管理费用', '消除所有渠道和物业的价格不一致', '为管理层提供跨物业实时绩效可见性', '30天内完成所有12个物业设置无停机'] },
    statsLabels: ['管理物业', '减少管理费用', '每物业增加', '跨物业数据'],
    testimonial: { quote: '在HotelStack.io之前，我需要三天来汇编月度绩效报告。现在我从一个仪表板即时看到一切。它改变了我们管理连锁的方式。', role: '运营副总裁', hotel: 'HotelStack Hotels & Resorts' },
    faqItems: [
      { question: '一个账户能管理多少物业？', answer: '无限制。连锁计划支持无限物业的集中管理。添加新物业无额外许可费用。' },
      { question: '可以为所有物业设置集中定价吗？', answer: '可以。集中定价管理允许从单一界面向所有物业推送费率、限制和套餐。' },
      { question: '能获得跨物业汇总报告吗？', answer: '可以。连锁级仪表板显示汇总和各物业指标。一键导出汇总报告。' },
      { question: '迁移多个物业需要多长时间？', answer: '我们通常每周迁移3-4个物业。12个物业的连锁可在30天内完全迁移。' },
    ],
    relatedIndustries: [
      { label: '度假村', description: '复杂的多网点度假村运营' },
      { label: '精品酒店', description: '定制品牌管理' },
      { label: '小型酒店', description: '单体物业的经济实惠PMS' },
      { label: '青年旅舍', description: '床位级经济管理' },
    ],
  },
};
